/**
 * Mode dégradé (CP-12, S77 C3) — le serveur est-il perdu ? Un seul état pour
 * tout l'onglet, hors des composants : la coque se remonte à chaque écran, et
 * un état local repartait à « tout va bien » au premier changement d'écran,
 * serveur toujours absent.
 *
 * Vrai quand le navigateur se dit hors ligne, quand la dernière requête a
 * échoué faute de réseau (câble branché, serveur injoignable), ou quand la
 * connexion temps réel est coupée depuis plus de `DELAI_AVANT_BANDEAU_MS`
 * (décision 416 : un écran au repos, où personne ne clique, le montre aussi). Faux dès que le
 * serveur répond quoi que ce soit — une réponse, ou un refus : un 422 dit
 * qu'il est là. Une donnée posée à la main dans le cache (`setQueryData`)
 * n'est pas une réponse et ne compte pas.
 */
import type { QueryClient } from '@tanstack/react-query'
import { estErreurApi } from '../../shared/api/erreurs.js'

// Node (tests) a un `navigator` sans `onLine` : on ne le lit que s'il existe.
let navigateurHorsLigne =
  typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine
let serveurEnPanne = false
let tempsReelPerdu = false
let minuterieTempsReel: ReturnType<typeof setTimeout> | null = null
const ecouteurs = new Set<() => void>()

/**
 * Une connexion temps réel coupée n'allume le bandeau qu'au-delà de ce délai :
 * la reconnexion retente en 1, 2 puis 4 s, et un redéploiement ou un hoquet
 * du Wi-Fi ne doit pas faire clignoter l'écran.
 */
export const DELAI_AVANT_BANDEAU_MS = 10_000

function changer(navigateur: boolean, panne: boolean, tempsReel = tempsReelPerdu): void {
  if (
    navigateur === navigateurHorsLigne &&
    panne === serveurEnPanne &&
    tempsReel === tempsReelPerdu
  )
    return
  navigateurHorsLigne = navigateur
  serveurEnPanne = panne
  tempsReelPerdu = tempsReel
  for (const ecouteur of ecouteurs) ecouteur()
}

function annulerMinuterieTempsReel(): void {
  if (minuterieTempsReel === null) return
  clearTimeout(minuterieTempsReel)
  minuterieTempsReel = null
}

export function serveurPerdu(): boolean {
  return navigateurHorsLigne || serveurEnPanne || tempsReelPerdu
}

export function abonner(ecouteur: () => void): () => void {
  ecouteurs.add(ecouteur)
  return () => {
    ecouteurs.delete(ecouteur)
  }
}

/**
 * L'état de la connexion temps réel.
 *
 * - `ouverte` : le serveur a répondu — le bandeau s'éteint, panne comprise ;
 * - `coupee` : le bandeau s'allume si elle ne revient pas dans le délai ;
 * - `arretee` : déconnexion ou session close — ce n'est pas une coupure.
 */
export function noterTempsReel(etat: 'ouverte' | 'coupee' | 'arretee'): void {
  if (etat === 'coupee') {
    if (tempsReelPerdu || minuterieTempsReel !== null) return
    minuterieTempsReel = setTimeout(() => {
      minuterieTempsReel = null
      changer(navigateurHorsLigne, serveurEnPanne, true)
    }, DELAI_AVANT_BANDEAU_MS)
    return
  }
  annulerMinuterieTempsReel()
  changer(navigateurHorsLigne, etat === 'ouverte' ? false : serveurEnPanne, false)
}

export function noterNavigateur(enLigne: boolean): void {
  changer(!enLigne, serveurEnPanne)
}

/** Une action du cache TanStack Query : requête ou mutation qui aboutit ou échoue. */
export function noterAction(action: { type: string; error?: unknown; manual?: boolean }): void {
  if (action.type === 'success' && action.manual !== true) changer(navigateurHorsLigne, false)
  if (action.type === 'error') {
    const reseau = estErreurApi(action.error) && action.error.code === 'RESEAU'
    // Une erreur qui n'est pas une ApiError (rendu, schéma) ne dit rien du serveur.
    if (reseau) changer(navigateurHorsLigne, true)
    else if (estErreurApi(action.error)) changer(navigateurHorsLigne, false)
  }
}

/** À brancher une fois, au démarrage, sur le client de l'application. */
export function suivreLeReseau(client: QueryClient): void {
  window.addEventListener('offline', () => {
    noterNavigateur(false)
  })
  window.addEventListener('online', () => {
    noterNavigateur(true)
  })
  client.getQueryCache().subscribe((evenement) => {
    if (evenement.type === 'updated') noterAction(evenement.action)
  })
  client.getMutationCache().subscribe((evenement) => {
    if (evenement.type === 'updated') noterAction(evenement.action)
  })
}
